<template>
  <div>
    <t-card v-for="sector in sectors" :key="sector.id" class="pb-3 pb-md-0 mb-4">
      <v-toolbar
        flat
        color="primary"
        density="compact"
      >
        <v-toolbar-title class="card-title-responsive">
          Setor: {{ sector.name }}
        </v-toolbar-title>
      </v-toolbar>
      <v-card
        color="grey-lighten-2 text-on-surface"
        elevation="0"
        class="mx-2 mt-4"
        style="padding: 2%"
      >
        <table class="table-mobile">
          <thead>
            <tr class="cabecalho">
              <th class="text-center font-weight-light">
                Qtd
              </th>
              <th class="text-left font-weight-light">
                {{ $t("entities.type") }}
              </th>
              <th class="text-right font-weight-light">
                Valor Unitário
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(type, index) in filteredTypes(sector)" :key="index">
              <tr :class="{'dashed-border': (cart.coupons.applied.get(type) ?? []).length <= 0}">
                <td style="width: 15%" class="text-center text-subtitle-1">
                  {{ type.quantidade }}
                </td>
                <td style="width: 35%" class="text-left text-subtitle-1">
                  {{ type.name }}
                </td>
                <td style="width: 50%; line-height: 16px" class="text-right text-subtitle-1">
                  {{ formatCurrency(type.current_batch?.price ?? 0) }}
                  <br>
                  <span style="font-size: 70%">
                    Taxa de Serviço:
                    {{
                      currency(getTypeInfo(type).convenienceFee)
                    }}
                  </span>
                </td>
              </tr>
              <tr v-for="(assoc) in getTypeInfo(type).coupons" :key="`coupons-${index}-${assoc.coupon.id}`" class="coupon-row">
                <td style="height: 20px !important">
                  <div class="d-flex justify-center">
                    <v-btn
                      icon="mdi-close"
                      size="small"
                      color="red"
                      variant="text"
                      @click="cart.coupons.remove(assoc.coupon)"
                    />
                  </div>
                </td>
                <td colspan="5" style="height: 20px !important">
                  <div class="d-flex justify-space-between text-caption" style="opacity: 0.7">
                    <div>{{ assoc.coupon.code }}</div>
                    <div v-if="assoc.coupon.method === 'absolute'" style="color: green">
                      -{{ currency(assoc.discount) }}
                    </div>
                    <div v-else style="color: green">
                      -{{ currency(assoc.discount) }}
                      ({{ assoc.coupon.value }}%)
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </v-card>
    </t-card>
    <t-card>
      <v-card color="grey-lighten-2">
        <v-col v-if="plan !== null && plan.interest > 0" class="d-flex justify-end pb-0">
          Juros do Parcelamento em {{ plan.installments }}x:&nbsp;
          <span>
            {{ currency(plan.interest / 100) }}
          </span>
        </v-col>
        <v-col class="d-flex justify-end">
          Total:&nbsp;
          <span class="font-weight-bold">
            {{ currency(total) }}
          </span>
        </v-col>
      </v-card>
    </t-card>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type Sector from "@/types/Sector";
import type Event from "@/types/Event";
import type TicketType from "@/types/TicketType";
import type Coupon from "@/types/Coupon";
import type InstallmentPlan from "@/types/InstallmentPlan";
import Fee from "@/utils/fees";
import TCard from "@/components/core/TCard.vue";
import { useEventsStore } from "@/stores/events";
import { useCartStore } from "@/stores/cart";
import { currency } from "@/filters";
import formatCurrency from "@/utils/formatCurrency";

interface Props {
  sectors: Sector[];
  /** The Installment plan chosen for paying by card, null until there is one. */
  plan?: InstallmentPlan | null;
}

const { sectors, plan = null } = defineProps<Props>();

const cart = useCartStore();

/** What the buyer pays: the chosen plan's amount carries its Buyer interest. */
const total = computed<number>(() => plan === null ? cart.totalAmount : plan.amount / 100);

const event = computed<Event | null>(() => useEventsStore().selected);

const filteredTypes = (sector: Sector) => {
  return sector.sections.flatMap(section => section.tickets).filter(t => t.quantidade > 0);
};

const getTypeInfo = (type: TicketType) => {
  if (event.value === null) {
    throw new Error("No event related to this payment");
  }

  const value = type.current_batch?.price ?? 0;

  const info = { ticket: type, coupons: [] as { coupon: Coupon; discount: number }[] };
  const prices = new Array<number>(type.quantidade).fill(value);

  for (const assoc of cart.coupons.applied.get(type) ?? []) {
    let discount = 0;
    for (let i = 0; i < assoc.uses; i++) {
      discount += cart.coupons.discount(prices[i], assoc.coupon);
      prices[i] -= cart.coupons.discount(prices[i], assoc.coupon);
    }
    info.coupons.push({ coupon: assoc.coupon, discount: discount });
  }

  const sum = prices.reduce((acc, price) => acc + price, 0);

  return {
    ...info,
    convenienceFee: Fee.calculate(sum, event.value.convenience_fee, event.value.convenience_fee_mode),
    subtotal: Fee.apply(sum, event.value.convenience_fee, event.value.convenience_fee_mode),
  };
};

defineOptions({
  name: "PaymentSummary",
});
</script>

<style>
.table-mobile {
  width: 100%;
  padding: 10%;
  border-collapse: collapse;
}
.table-mobile thead .cabecalho {
  border-bottom: 1px solid grey;
}
.table-mobile thead .cabecalho th {
  font-size: 0.8rem;
}
.table-mobile tbody tr.dashed-border:not(:last-child) {
  border-bottom: 1px dashed rgba(0, 0, 0, 0.87);
}
.table-mobile tbody tr.coupon-row td {
  padding-top: 0;
}
.table-mobile tbody tr td {
  padding: 0.8rem 0 0.2rem 0;
}
</style>
